import { isAxiosError } from "axios";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { restoreText } from "../pages/Careers/careersFormat";
import { type CustomPrintoutGenerationRequest, generationRequestBody } from "../pages/DaySchedule/CustomPrintout/customPrintoutRequests";
import type { CustomPrintoutCoverage, CustomPrintoutGenerationResponse } from "../pages/DaySchedule/CustomPrintout/customPrintoutTypes";
import { backendService } from "../services/backend.service";
import { errorText, uiVisibleErrorKey } from "../utils/errors";

/** How often a running design job is asked whether it's done. */
export const GENERATION_POLL_INTERVAL_MS = 2000;
/** Network blips a 2-minute job may ride out between two answers before it is given up on. */
export const MAX_CONSECUTIVE_POLL_FAILURES = 3;
/** Shown when a failure carries no message of its own (a network error, a 5xx, a malformed answer). */
export const GENERATION_FAILED_KEY = "printout.custom.ai.generate.failed";
/** Below this an HTTP status is the server answering "no" for good; retrying the poll won't change it. */
const SERVER_ERROR_STATUS = 500;

export type CustomPrintoutGenerationOutcome =
	| { status: "succeeded"; generationId: string; source: string; summary: string; coverage: CustomPrintoutCoverage }
	/** The request reads as a summary the user hasn't confirmed: nothing was designed, and `summary` is the sheet Annie would make. */
	| { status: "needsConfirmation"; summary: string }
	/** `errorKey` is an i18n key: the job's own (`error.printout.custom.*`) or `GENERATION_FAILED_KEY`. */
	| { status: "failed"; errorKey: string }
	/** Cancelled or unmounted: nobody is waiting for the answer any more. */
	| { status: "cancelled" };

/** A job stopped by a cancel. Never shown: whoever cancelled it has already moved on. */
const CANCELLED_ERROR_KEY = "error.printout.custom.cancelled";

const wait = (ms: number, signal: AbortSignal): Promise<void> =>
	new Promise((resolve) => {
		const timer = setTimeout(resolve, ms);
		signal.addEventListener(
			"abort",
			() => {
				clearTimeout(timer);
				resolve();
			},
			{ once: true },
		);
	});

const isPermanent = (error: unknown): boolean => isAxiosError(error) && error.response !== undefined && error.response.status < SERVER_ERROR_STATUS;

const failureKeyOf = (error: unknown): string => {
	const key = uiVisibleErrorKey(error);
	// DEFAULT: a network error, a 5xx or a malformed answer carries no message of its own; say it generically.
	return key === undefined ? GENERATION_FAILED_KEY : key;
};

/** A finished job's answer, or `null` while it runs. A finished answer missing its parts is a server bug: thrown. */
const settledOutcomeOf = (data: CustomPrintoutGenerationResponse): CustomPrintoutGenerationOutcome | null => {
	if (data.status === "running") return null;
	if (data.status === "succeeded") {
		if (data.source === null || data.summary === null || data.coverage === null) {
			throw new Error(`generation ${data.generationId} succeeded without a source, summary or coverage`);
		}
		// Annie writes the summary, so it can be a bare date that `axiosInstance` turned into a DateTime (`customPrintoutText`).
		return { status: "succeeded", generationId: data.generationId, source: data.source, summary: restoreText(data.summary), coverage: data.coverage };
	}
	if (data.status === "awaiting_confirmation") {
		if (data.summary === null) throw new Error(`generation ${data.generationId} is awaiting confirmation without a summary`);
		return { status: "needsConfirmation", summary: restoreText(data.summary) };
	}
	if (data.errorKey === null) throw new Error(`generation ${data.generationId} failed without an error key`);
	return data.errorKey === CANCELLED_ERROR_KEY ? { status: "cancelled" } : { status: "failed", errorKey: data.errorKey };
};

/**
 * Starts a custom printout design job and polls it every 2 s until it finishes. A job takes up to about two
 * minutes, which is why it's a job: a request that long would be cut off on the way.
 *
 * `run` resolves with the outcome rather than throwing, so the caller's state machine has one place to go on
 * from. `cancel` (and unmounting) stops waiting, resolves the pending run as `cancelled`, and tells the server to
 * stop the job, so the user's next request isn't refused as "still working". A cancel during the upload aborts it
 * and no job is started; a cancel while the start request is in flight cancels the job as soon as its id is known.
 */
export const useCustomPrintoutGeneration = () => {
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const controllerRef = useRef<AbortController | null>(null);
	/** The running job's id once the server has given it, so a cancel can reach the server too. */
	const generationIdRef = useRef<string | null>(null);

	const cancelOnServer = useCallback(
		(generationId: string) => {
			// Fire and forget: the user has already moved on, and a job that isn't cancelled times out on its own.
			backendService.cancelCustomPrintoutGeneration(departmentId, generationId).catch((error: unknown) => {
				trackError("custom_printout_generate_cancel", { department_id: departmentId, generation_id: generationId, error_message: errorText(error) });
			});
		},
		[departmentId, trackError],
	);

	const cancel = useCallback(() => {
		controllerRef.current?.abort();
		controllerRef.current = null;
		if (generationIdRef.current !== null) cancelOnServer(generationIdRef.current);
		generationIdRef.current = null;
	}, [cancelOnServer]);
	useEffect(() => cancel, [cancel]);

	const poll = useCallback(
		async (generationId: string, signal: AbortSignal): Promise<CustomPrintoutGenerationOutcome> => {
			let failures = 0;
			while (!signal.aborted) {
				await wait(GENERATION_POLL_INTERVAL_MS, signal);
				if (signal.aborted) break;
				let data: CustomPrintoutGenerationResponse;
				try {
					data = (await backendService.getCustomPrintoutGeneration(departmentId, generationId)).data;
				} catch (error) {
					failures += 1;
					if (isPermanent(error) || failures >= MAX_CONSECUTIVE_POLL_FAILURES) throw error;
					continue;
				}
				// Cancelled while the answer was on its way: whatever it says, nobody is waiting for it.
				if (signal.aborted) break;
				failures = 0;
				const outcome = settledOutcomeOf(data);
				if (outcome !== null) return outcome;
			}
			return { status: "cancelled" };
		},
		[departmentId],
	);

	/** Uploads the example (abortable), then starts the job unless the caller has gone; returns the job's id or `null`. */
	const start = useCallback(
		async (request: CustomPrintoutGenerationRequest, signal: AbortSignal): Promise<string | null> => {
			const fileKey = request.file === null ? null : (await backendService.uploadPrintoutExampleFile(departmentId, request.file, signal)).data.fileKey;
			if (signal.aborted) return null;
			const { data } = await backendService.startCustomPrintoutGeneration(generationRequestBody(departmentId, request, fileKey));
			// Not aborted mid-flight on purpose: the server may already have started the job, and only its id lets us stop it.
			if (signal.aborted) cancelOnServer(data.generationId);
			return signal.aborted ? null : data.generationId;
		},
		[departmentId, cancelOnServer],
	);

	const run = useCallback(
		async (request: CustomPrintoutGenerationRequest): Promise<CustomPrintoutGenerationOutcome> => {
			cancel();
			const controller = new AbortController();
			controllerRef.current = controller;
			const props = {
				department_id: departmentId,
				printout_id: request.printoutId,
				has_file: request.file !== null,
				prompt_length: request.prompt === null ? 0 : request.prompt.length,
				is_repair: request.repair !== null,
				effort: request.effort,
				allows_summary: request.allowsSummary,
			};
			const startedAt = DateTime.now();
			const durationMs = () => Math.round(DateTime.now().diff(startedAt).as("milliseconds"));
			track("custom_printout_generate_start", props);
			try {
				const generationId = await start(request, controller.signal);
				if (generationId === null) return { status: "cancelled" };
				generationIdRef.current = generationId;
				const outcome = await poll(generationId, controller.signal);
				if (outcome.status === "succeeded") track("custom_printout_generate_success", { ...props, duration_ms: durationMs(), coverage: outcome.coverage });
				if (outcome.status === "needsConfirmation") track("custom_printout_generate_summary_question", { ...props, duration_ms: durationMs() });
				if (outcome.status === "failed") track("custom_printout_generate_error", { ...props, duration_ms: durationMs(), error_key: outcome.errorKey });
				return outcome;
			} catch (error) {
				if (controller.signal.aborted) return { status: "cancelled" };
				track("custom_printout_generate_error", { ...props, duration_ms: durationMs(), error_key: failureKeyOf(error) });
				trackError("custom_printout_generate", { ...props, error_message: errorText(error) });
				return { status: "failed", errorKey: failureKeyOf(error) };
			} finally {
				// Finished on its own: nothing left to cancel. (A newer run has already replaced both refs.)
				if (controllerRef.current === controller) {
					controllerRef.current = null;
					generationIdRef.current = null;
				}
			}
		},
		[cancel, departmentId, poll, start, track, trackError],
	);

	return { run, cancel };
};
